import React from 'react';
import { getBodyPartColor, getBodyPartOpacity, getBodyPartForView } from '../utils';

/**
 * Interactive SVG body diagram component
 * @param {Object} props - Component props
 * @param {string} props.selectedBodyPart - Currently selected body part
 * @param {Function} props.onBodyPartClick - Handler for body part clicks
 * @param {string} props.viewType - 'front' or 'back'
 * @param {Array} props.molesData - Array of mole data for coloring
 * @param {boolean} props.isCreateMode - Whether in create mode
 */
const InteractiveBodySVG = ({
  selectedBodyPart,
  onBodyPartClick,
  viewType,
  molesData = [],
  isCreateMode = false,
}) => {
  const getClickHandler = (frontPart) => {
    const actualPart = getBodyPartForView(frontPart, viewType);
    return () => onBodyPartClick(actualPart);
  };

  // Get fill color based on mode
  const getFillColor = (frontPart) => {
    const actualPart = getBodyPartForView(frontPart, viewType);
    if (isCreateMode) {
      return '#28a745'; // Green color for create mode
    }
    

    
    return getBodyPartColor(actualPart, molesData);
  };

  // Get opacity based on mode
  const getFillOpacity = (frontPart) => {
    const actualPart = getBodyPartForView(frontPart, viewType);
    if (isCreateMode) {
      return 0.3; // Lower opacity in create mode for better visibility
    }
    return getBodyPartOpacity(actualPart, selectedBodyPart);
  };

  return (
    <div className='interactive-body-svg'>
      <svg
        viewBox='0 0 278.8 720'
        style={{
          width: '100%',
          maxWidth: '300px',
          cursor: isCreateMode ? 'crosshair' : 'pointer',
        }}
      >
        {/* Head */}
        <ellipse
          cx='140'
          cy='75'
          rx='25'
          ry='35'
          fill={getFillColor('head')}
          fillOpacity={getFillOpacity('head')}
          stroke='#495057'
          strokeWidth='2'
          onClick={getClickHandler('head')}
          style={{ cursor: 'pointer' }}
        />

        {/* Face - only show in front view */}
        {viewType === 'front' && (
          <ellipse
            cx='140'
            cy='90'
            rx='20'
            ry='25'
            fill={getFillColor('face')}
            fillOpacity={getFillOpacity('face')}
            stroke='#495057'
            strokeWidth='2'
            onClick={getClickHandler('face')}
            style={{ cursor: 'pointer' }}
          />
        )}

        {/* Neck */}
        <rect
          x='125'
          y='115'
          width='30'
          height='25'
          fill={getFillColor('neck')}
          fillOpacity={getFillOpacity('neck')}
          stroke='#495057'
          strokeWidth='2'
          onClick={getClickHandler('neck')}
          style={{ cursor: 'pointer' }}
        />

        {/* Chest/Upper Back */}
        <ellipse
          cx='140'
          cy='200'
          rx='50'
          ry='60'
          fill={getFillColor('chest')}
          fillOpacity={getFillOpacity('chest')}
          stroke='#495057'
          strokeWidth='2'
          onClick={getClickHandler('chest')}
          style={{ cursor: 'pointer' }}
        />

        {/* Abdomen/Lower Back */}
        <ellipse
          cx='140'
          cy='300'
          rx='40'
          ry='50'
          fill={getFillColor('abdomen')}
          fillOpacity={getFillOpacity('abdomen')}
          stroke='#495057'
          strokeWidth='2'
          onClick={getClickHandler('abdomen')}
          style={{ cursor: 'pointer' }}
        />

        {/* Left Upper Arm */}
        <ellipse
          cx='75'
          cy='200'
          rx='15'
          ry='50'
          fill={getFillColor('left_upper_arm')}
          fillOpacity={getFillOpacity('left_upper_arm')}
          stroke='#495057'
          strokeWidth='2'
          onClick={getClickHandler('left_upper_arm')}
          style={{ cursor: 'pointer' }}
        />

        {/* Right Upper Arm */}
        <ellipse
          cx='205'
          cy='200'
          rx='15'
          ry='50'
          fill={getFillColor('right_upper_arm')}
          fillOpacity={getFillOpacity('right_upper_arm')}
          stroke='#495057'
          strokeWidth='2'
          onClick={getClickHandler('right_upper_arm')}
          style={{ cursor: 'pointer' }}
        />

        {/* Left Forearm */}
        <ellipse
          cx='70'
          cy='280'
          rx='12'
          ry='40'
          fill={getFillColor('left_forearm')}
          fillOpacity={getFillOpacity('left_forearm')}
          stroke='#495057'
          strokeWidth='2'
          onClick={getClickHandler('left_forearm')}
          style={{ cursor: 'pointer' }}
        />

        {/* Right Forearm */}
        <ellipse
          cx='210'
          cy='280'
          rx='12'
          ry='40'
          fill={getFillColor('right_forearm')}
          fillOpacity={getFillOpacity('right_forearm')}
          stroke='#495057'
          strokeWidth='2'
          onClick={getClickHandler('right_forearm')}
          style={{ cursor: 'pointer' }}
        />

        {/* Left Hand */}
        <circle
          cx='65'
          cy='330'
          r='12'
          fill={getFillColor('left_hand')}
          fillOpacity={getFillOpacity('left_hand')}
          stroke='#495057'
          strokeWidth='2'
          onClick={getClickHandler('left_hand')}
          style={{ cursor: 'pointer' }}
        />

        {/* Right Hand */}
        <circle
          cx='215'
          cy='330'
          r='12'
          fill={getFillColor('right_hand')}
          fillOpacity={getFillOpacity('right_hand')}
          stroke='#495057'
          strokeWidth='2'
          onClick={getClickHandler('right_hand')}
          style={{ cursor: 'pointer' }}
        />

        {/* Left Thigh */}
        <ellipse
          cx='120'
          cy='450'
          rx='25'
          ry='80'
          fill={getFillColor('left_thigh')}
          fillOpacity={getFillOpacity('left_thigh')}
          stroke='#495057'
          strokeWidth='2'
          onClick={getClickHandler('left_thigh')}
          style={{ cursor: 'pointer' }}
        />

        {/* Right Thigh */}
        <ellipse
          cx='160'
          cy='450'
          rx='25'
          ry='80'
          fill={getFillColor('right_thigh')}
          fillOpacity={getFillOpacity('right_thigh')}
          stroke='#495057'
          strokeWidth='2'
          onClick={getClickHandler('right_thigh')}
          style={{ cursor: 'pointer' }}
        />

        {/* Left Lower Leg */}
        <ellipse
          cx='115'
          cy='600'
          rx='18'
          ry='70'
          fill={getFillColor('left_lower_leg')}
          fillOpacity={getFillOpacity('left_lower_leg')}
          stroke='#495057'
          strokeWidth='2'
          onClick={getClickHandler('left_lower_leg')}
          style={{ cursor: 'pointer' }}
        />

        {/* Right Lower Leg */}
        <ellipse
          cx='165'
          cy='600'
          rx='18'
          ry='70'
          fill={getFillColor('right_lower_leg')}
          fillOpacity={getFillOpacity('right_lower_leg')}
          stroke='#495057'
          strokeWidth='2'
          onClick={getClickHandler('right_lower_leg')}
          style={{ cursor: 'pointer' }}
        />

        {/* Left Foot */}
        <ellipse
          cx='110'
          cy='690'
          rx='15'
          ry='25'
          fill={getFillColor('left_foot')}
          fillOpacity={getFillOpacity('left_foot')}
          stroke='#495057'
          strokeWidth='2'
          onClick={getClickHandler('left_foot')}
          style={{ cursor: 'pointer' }}
        />

        {/* Right Foot */}
        <ellipse
          cx='170'
          cy='690'
          rx='15'
          ry='25'
          fill={getFillColor('right_foot')}
          fillOpacity={getFillOpacity('right_foot')}
          stroke='#495057'
          strokeWidth='2'
          onClick={getClickHandler('right_foot')}
          style={{ cursor: 'pointer' }}
        />

        {/* Hips */}
        <ellipse
          cx='140'
          cy='370'
          rx='45'
          ry='30'
          fill={getFillColor('hips')}
          fillOpacity={getFillOpacity('hips')}
          stroke='#495057'
          strokeWidth='2'
          onClick={getClickHandler('hips')}
          style={{ cursor: 'pointer' }}
        />
      </svg>
    </div>
  );
};

export default InteractiveBodySVG;
